import { useEffect } from 'react'
import { vendorDashboardApi } from '@cravo/api-service'
import { emitDashboardData, emitPendingRequestsCount } from '@/lib/dashboard-events'
import { DEFAULT_ACCEPT_TIMEOUT_MIN, getRequestExpiry } from '@/lib/order-helpers'

const POLL_INTERVAL_MS = 20_000
// Small delay before the first poll so it lands after the page's own
// initial load (which seeds lists + alert baselines) instead of racing it.
const INITIAL_POLL_DELAY_MS = 2_000

/**
 * Layout-level dashboard poller — the single source of "are there new
 * orders?" for the whole app.
 *
 * Previously only RequestsPage and DashboardPage polled, and each emitted
 * PENDING_REQUESTS_EVENT from its own interval. The moment the vendor
 * landed anywhere else — most importantly /orders, where Accept
 * auto-navigates them — nothing polled: the sidebar badge went stale and
 * useNewOrderAlerts (sound + browser notification + title flash) never
 * fired. During a multi-order rush that meant order #2 and #3 could sit
 * unnoticed until their accept-timeout expired.
 *
 * This hook runs once in DashboardLayout (inside AuthGuard+BusinessGuard,
 * so the session is valid) and broadcasts two events on every poll:
 *   - PENDING_REQUESTS_EVENT  → Sidebar/TopNav badges + useNewOrderAlerts
 *   - DASHBOARD_DATA_EVENT    → DashboardPage / RequestsPage list refresh
 *
 * Pages no longer need their own intervals — they subscribe to
 * DASHBOARD_DATA_EVENT instead, so there is exactly ONE dashboard fetch
 * per interval regardless of which page is open.
 *
 * Two consistency rules baked in here:
 *   1. Client-expired pending requests are filtered out BEFORE emitting.
 *      The pages remove a request locally when its accept-countdown hits
 *      zero; if the backend's auto-expiry cron lags, the raw payload
 *      would resurrect that card every 20s — re-triggering the new-order
 *      sound/notification for an order nobody can accept any more.
 *   2. The event carries `fetchedAt` (when the request STARTED) so pages
 *      can drop payloads that pre-date their latest accept/decline and
 *      would otherwise resurrect an optimistically removed card.
 */
export function useDashboardPoller() {
  useEffect(() => {
    let disposed = false

    const poll = async () => {
      if (disposed || document.visibilityState === 'hidden') return
      const fetchedAt = Date.now()
      try {
        const response = await vendorDashboardApi.get()
        if (disposed) return

        const timeoutMin = response.vendorAcceptTimeoutMin ?? DEFAULT_ACCEPT_TIMEOUT_MIN
        const rawPending = response.pendingRequests ?? []
        const livePending = rawPending.filter((order) => {
          const expiry = getRequestExpiry(order, timeoutMin)
          return !expiry || expiry.getTime() > Date.now()
        })
        const payload =
          livePending.length === rawPending.length
            ? response
            : { ...response, pendingRequests: livePending }

        emitPendingRequestsCount(livePending.length)
        emitDashboardData(payload, fetchedAt)
      } catch {
        // Silent — pages surface persistent failures via their own loads.
      }
    }

    const initialTimer = window.setTimeout(() => void poll(), INITIAL_POLL_DELAY_MS)
    const interval = window.setInterval(() => void poll(), POLL_INTERVAL_MS)
    const handleVisibility = () => {
      // Catch up immediately when the vendor returns to the tab instead
      // of waiting out the remainder of the interval.
      if (document.visibilityState === 'visible') void poll()
    }
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      disposed = true
      window.clearTimeout(initialTimer)
      window.clearInterval(interval)
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [])
}
